import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { GripVertical, Settings } from 'lucide-react';

const COLUMN_MENU_WIDTH = 256;
const COLUMN_MENU_OFFSET = 8;

/**
 * Column visibility gear menu for RCM tables (Process + ITGC).
 * Same UX as Controls / Risks: show/hide, optional drag-reorder of checked columns.
 */
export function RcmColumnVisibilityMenu({
  columns,
  columnVisibility,
  onToggleColumn,
  onReset,
  ariaLabel = 'Show or hide columns',
  enableReorder = false,
  columnOrder = null,
  onReorder,
}) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState(null);
  const [dragKey, setDragKey] = useState(null);
  const triggerRef = useRef(null);
  const popupRef = useRef(null);
  const listScrollRef = useRef(null);
  const dragScrollDirectionRef = useRef(0);
  const dragScrollRafRef = useRef(null);

  const stopDragAutoScroll = useCallback(() => {
    dragScrollDirectionRef.current = 0;
    if (dragScrollRafRef.current != null) {
      cancelAnimationFrame(dragScrollRafRef.current);
      dragScrollRafRef.current = null;
    }
  }, []);

  const ensureDragAutoScrollLoop = useCallback(() => {
    if (dragScrollRafRef.current != null) return;
    const tick = () => {
      const direction = dragScrollDirectionRef.current;
      const list = listScrollRef.current;
      if (!direction || !list) {
        dragScrollRafRef.current = null;
        return;
      }
      list.scrollTop += direction * 10;
      dragScrollRafRef.current = requestAnimationFrame(tick);
    };
    dragScrollRafRef.current = requestAnimationFrame(tick);
  }, []);

  const updateDragAutoScrollFromClientY = useCallback(
    (clientY) => {
      const list = listScrollRef.current;
      if (!list) return;
      const rect = list.getBoundingClientRect();
      const edge = 36;
      if (clientY < rect.top + edge) {
        dragScrollDirectionRef.current = -1;
        ensureDragAutoScrollLoop();
      } else if (clientY > rect.bottom - edge) {
        dragScrollDirectionRef.current = 1;
        ensureDragAutoScrollLoop();
      } else {
        stopDragAutoScroll();
      }
    },
    [ensureDragAutoScrollLoop, stopDragAutoScroll],
  );

  useEffect(() => () => stopDragAutoScroll(), [stopDragAutoScroll]);

  const orderedColumns = useMemo(() => {
    if (!enableReorder || !Array.isArray(columnOrder) || columnOrder.length === 0) {
      return columns;
    }
    const byId = new Map(columns.map((col) => [col.id, col]));
    const ordered = [];
    for (const id of columnOrder) {
      const col = byId.get(id);
      if (!col) continue;
      ordered.push(col);
      byId.delete(id);
    }
    for (const col of byId.values()) ordered.push(col);
    return ordered;
  }, [columns, columnOrder, enableReorder]);

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const desiredLeft = rect.right - COLUMN_MENU_WIDTH;
    const maxLeft = Math.max(
      COLUMN_MENU_OFFSET,
      window.innerWidth - COLUMN_MENU_WIDTH - COLUMN_MENU_OFFSET,
    );
    const left = Math.min(Math.max(COLUMN_MENU_OFFSET, desiredLeft), maxLeft);
    setPosition({ top: rect.bottom + COLUMN_MENU_OFFSET, left });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    updatePosition();
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event) => {
      const trigger = triggerRef.current;
      const popup = popupRef.current;
      const target = event.target;
      if (trigger && trigger.contains(target)) return;
      if (popup && popup.contains(target)) return;
      stopDragAutoScroll();
      setOpen(false);
    };
    const onReposition = () => updatePosition();
    document.addEventListener('mousedown', onPointerDown);
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [open, updatePosition, stopDragAutoScroll]);

  const handleReset = useCallback(() => {
    onReset?.();
    setOpen(false);
  }, [onReset]);

  const isColumnChecked = useCallback(
    (columnId) => columnVisibility?.[columnId] !== false,
    [columnVisibility],
  );

  const handleDragStart = useCallback(
    (key, canDrag) => (e) => {
      if (!enableReorder || !canDrag) {
        e.preventDefault();
        return;
      }
      setDragKey(key);
      e.dataTransfer.effectAllowed = 'move';
      try {
        e.dataTransfer.setData('text/plain', key);
      } catch (_) {
        /* ignore */
      }
    },
    [enableReorder],
  );

  const handleListDragOver = useCallback(
    (e) => {
      if (!enableReorder || !dragKey) return;
      e.preventDefault();
      updateDragAutoScrollFromClientY(e.clientY);
    },
    [dragKey, enableReorder, updateDragAutoScrollFromClientY],
  );

  const handleDragOver = useCallback(
    (overKey, overCanDrop) => (e) => {
      if (!enableReorder || !onReorder || !overCanDrop) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      updateDragAutoScrollFromClientY(e.clientY);
      if (!dragKey || dragKey === overKey) return;

      const visibleIds = orderedColumns
        .filter((col) => isColumnChecked(col.id))
        .map((col) => col.id);
      if (!visibleIds.includes(dragKey) || !visibleIds.includes(overKey)) return;

      const nextVisible = [...visibleIds];
      const fromIdx = nextVisible.indexOf(dragKey);
      const toIdx = nextVisible.indexOf(overKey);
      if (fromIdx < 0 || toIdx < 0) return;
      nextVisible.splice(fromIdx, 1);
      nextVisible.splice(toIdx, 0, dragKey);

      let visibleCursor = 0;
      const nextFull = orderedColumns.map((col) => {
        if (!isColumnChecked(col.id)) return col.id;
        const nextId = nextVisible[visibleCursor];
        visibleCursor += 1;
        return nextId ?? col.id;
      });
      onReorder(nextFull);
    },
    [
      dragKey,
      enableReorder,
      isColumnChecked,
      onReorder,
      orderedColumns,
      updateDragAutoScrollFromClientY,
    ],
  );

  const handleDragEnd = useCallback(() => {
    stopDragAutoScroll();
    setDragKey(null);
  }, [stopDragAutoScroll]);

  const popup =
    open && position
      ? createPortal(
          <div
            ref={popupRef}
            data-column-visibility-menu
            className="fixed w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-50"
            style={{ top: position.top, left: position.left }}
          >
            <div className="px-3 py-2 border-b border-gray-100">
              <p className="text-sm font-semibold text-gray-900">Show columns</p>
              <p className="text-xs text-gray-500">
                {enableReorder
                  ? 'Select columns to display, or drag checked columns to reorder.'
                  : 'Select columns to display in this table.'}
              </p>
            </div>
            <div
              ref={listScrollRef}
              className="p-3 space-y-1 max-h-72 overflow-auto"
              onDragOver={handleListDragOver}
            >
              {orderedColumns.map((column) => {
                const isChecked = isColumnChecked(column.id);
                const canDrag = enableReorder && isChecked;
                const isDragging = dragKey === column.id;
                return (
                  <label
                    key={column.id}
                    draggable={canDrag}
                    onDragStart={handleDragStart(column.id, canDrag)}
                    onDragOver={handleDragOver(column.id, canDrag)}
                    onDragEnd={handleDragEnd}
                    onDrop={(e) => e.preventDefault()}
                    className={`flex items-center gap-2 text-sm text-gray-700 py-0.5 ${
                      canDrag ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'
                    } ${isDragging ? 'opacity-50 bg-blue-50 rounded' : ''} ${
                      !isChecked ? 'text-gray-400' : ''
                    }`}
                  >
                    {enableReorder ? (
                      <GripVertical
                        className={`h-3.5 w-3.5 shrink-0 ${canDrag ? 'text-gray-400' : 'text-gray-200'}`}
                        aria-hidden
                      />
                    ) : null}
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => onToggleColumn(column.id)}
                      className="h-4 w-4 cursor-pointer rounded border border-gray-300 text-blue-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                    />
                    <span className="flex-1 truncate">{column.label}</span>
                  </label>
                );
              })}
            </div>
            <div className="px-3 py-2 border-t border-gray-100 flex justify-end">
              <button
                type="button"
                onClick={handleReset}
                className="text-xs font-medium text-blue-700 hover:text-blue-900"
              >
                Reset to default
              </button>
            </div>
          </div>,
          document.body,
        )
      : null;

  return (
    <>
      <div className="shrink-0" data-column-visibility-menu>
        <button
          ref={triggerRef}
          type="button"
          onClick={() => setOpen((prev) => !prev)}
          aria-label={ariaLabel}
          aria-expanded={open}
          className="inline-flex items-center justify-center h-8 w-8 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          <Settings className="h-4 w-4" aria-hidden />
        </button>
      </div>
      {popup}
    </>
  );
}
